import { useState, useEffect, lazy, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import SplashScreen from "@/components/SplashScreen";
import { Skeleton } from "@/components/ui/skeleton";
import BottomNavigation from "@/components/BottomNavigation";
import TopNavbar from "@/components/TopNavbar";
import ErrorBoundary from "@/components/ErrorBoundary";
import FinFoxMascot from "@/components/FinFoxMascot";
import FinFoxRunner from "@/components/FinFoxRunner";
import FinFoxNotices from "@/components/FinFoxNotices";
import FinFoxDailyQuestion from "@/components/FinFoxDailyQuestion";
import DebugPanel from "@/components/DebugPanel";
import { prefetchLikelyRoutes } from "@/components/PrefetchLink";


// Lazy load all route components for code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FinancialTinderPage = lazy(() => import("./pages/FinancialTinderPage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const CommunityPostPage = lazy(() => import("./pages/CommunityPostPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const FinancialLeaguePage = lazy(() => import("./pages/FinancialLeaguePage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ProfessorDashboard = lazy(() => import("./pages/ProfessorDashboard"));
const CreateBatchPage = lazy(() => import("./pages/CreateBatchPage"));
const BatchDetailPage = lazy(() => import("./pages/BatchDetailPage"));
const RewardsPage = lazy(() => import("./pages/RewardsPage"));
const AnalyticsDashboard = lazy(() => import("./pages/AnalyticsDashboard"));
const ContentManagement = lazy(() => import("./pages/ContentManagement"));
const InstitutionAdminDashboard = lazy(() => import("./pages/InstitutionAdminDashboard"));
const AIFeaturesPage = lazy(() => import("./pages/AIFeaturesPage"));
const BusinessInsightsPage = lazy(() => import("./pages/BusinessInsightsPage"));
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const TradingJournalPage = lazy(() => import("./pages/TradingJournalPage"));
const InterviewPrepPage = lazy(() => import("./pages/InterviewPrepPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const MoneyBasicsPage = lazy(() => import("./pages/MoneyBasicsPage"));


// Route loading fallback component
const RouteLoadingFallback = () => (
  <div className="min-h-[60vh] bg-background flex items-center justify-center">
    <div className="w-full max-w-md space-y-4 p-8">
      <Skeleton className="h-8 w-3/4 mx-auto" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-32 w-full mt-6" />
      <div className="grid grid-cols-2 gap-4 mt-4">
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </div>
    </div>
  </div>
);


const queryClient = new QueryClient();

// Sibling-route prefetch: given the current route, warm the chunks the user
// is most likely to open next. Purely additive (chunk imports only), no data.
const SIBLING_ROUTES: Record<string, string[]> = {
  "/": ["/financial-tinder", "/financial-league", "/community", "/dashboard"],
  "/dashboard": ["/rewards-center", "/trading-journal", "/financial-league", "/financial-tinder"],
  "/financial-league": ["/leaderboard", "/trading-journal", "/dashboard"],
  "/financial-tinder": ["/dashboard", "/community"],
  "/community": ["/dashboard", "/financial-tinder"],
  "/leaderboard": ["/financial-league", "/dashboard"],
  "/rewards-center": ["/dashboard", "/profile"],
  "/profile": ["/dashboard", "/rewards-center"],
  "/trading-journal": ["/financial-league", "/dashboard"],
};

const AppContent = () => {
  const location = useLocation();

  useEffect(() => {
    const siblings = SIBLING_ROUTES[location.pathname];
    if (siblings?.length) prefetchLikelyRoutes(siblings);
  }, [location.pathname]);

  return (

    <div className="min-h-screen flex flex-col w-full">
      <TopNavbar />
      <FinFoxRunner />
      <FinFoxNotices />
      <FinFoxDailyQuestion />
      
      <main className="flex-1 min-w-0 pt-16">
        <Suspense fallback={<RouteLoadingFallback />}>
          <ErrorBoundary>
          <div className="animate-fade-in">
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/financial-tinder" element={<FinancialTinderPage />} />
            <Route path="/tinder" element={<Navigate to="/financial-tinder" replace />} />
            <Route path="/financial-league" element={<FinancialLeaguePage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/community/post/:postId" element={<CommunityPostPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/:userId" element={<PublicProfilePage />} />
            <Route path="/professor" element={<ProfessorDashboard />} />
            <Route path="/professor/create-batch" element={<CreateBatchPage />} />
            <Route path="/professor/batch/:batchId" element={<BatchDetailPage />} />
            <Route path="/rewards" element={<Navigate to="/rewards-center" replace />} />
            <Route path="/rewards-center" element={<RewardsPage />} />
            <Route path="/install" element={<Navigate to="/" replace />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />
            <Route path="/professor/content" element={<ContentManagement />} />
            <Route path="/institution-admin" element={<InstitutionAdminDashboard />} />
            <Route path="/ai-features" element={<AIFeaturesPage />} />
            <Route path="/business-insights" element={<BusinessInsightsPage />} />
            <Route path="/trading-journal" element={<TradingJournalPage />} />
            <Route path="/interview-prep" element={<InterviewPrepPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/money-basics" element={<MoneyBasicsPage />} />

            <Route path="/faq" element={<HelpPage />} />
            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </div>
          </ErrorBoundary>
        </Suspense>
        
        <BottomNavigation />
        <FinFoxMascot />
        <div className="md:hidden h-16" />
      </main>
    </div>
  );
};

const App = () => {
  const [showSplash, setShowSplash] = useState(() => {
    const hasVisitedBefore = localStorage.getItem('finplay_visited') === 'true';
    const hasSeenThisSession = sessionStorage.getItem('finplay-splash-shown') === 'true';
    return !hasVisitedBefore && !hasSeenThisSession;
  });

  // Auto-skip splash for logged-in users
  useEffect(() => {
    if (!showSplash) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        localStorage.setItem('finplay_visited', 'true');
        sessionStorage.setItem('finplay-splash-shown', 'true');
        setShowSplash(false);
      }
    });
  }, [showSplash]);

  const handleSplashComplete = () => {
    sessionStorage.setItem('finplay-splash-shown', 'true');
    localStorage.setItem('finplay_visited', 'true');
    setShowSplash(false);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
          <DebugPanel />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
